<template>
  <v-container fluid>
    <h2 class="page-title mb-4">Gross Margin</h2>

    <!-- Resumen: siniestralidad total y por línea -->
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="Gross Margin (total)"
          :firstValue="grossMarginTotal.pctLabel"
          :secondValue="grossMarginTotal.detailLabel"
          :showCsvButton="false"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="Siniestralidad · B2B Asistencias"
          :firstValue="siniestralidadAsistencias.pctLabel"
          :secondValue="siniestralidadAsistencias.detailLabel"
          :showCsvButton="false"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="Siniestralidad · B2B Employee Benefit"
          :firstValue="siniestralidadEmployeeBenefit.pctLabel"
          :secondValue="siniestralidadEmployeeBenefit.detailLabel"
          :showCsvButton="false"
        />
      </v-col>
    </v-row>

    <!-- Filtros: dos líneas para evitar que los selects múltiples se amontonen -->
    <v-card outlined class="filters-card mt-6 pa-4">
      <v-row dense align="center">
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedCountryId"
            :items="countries"
            item-text="text"
            item-value="id"
            :loading="loadingCountries"
            label="País"
            placeholder="Selecciona un país"
            dense
            hide-details
            outlined
            @change="loadData"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedLineas"
            :items="lineaOptions"
            label="Línea de negocio"
            multiple
            dense
            hide-details
            outlined
            class="single-line-select"
            @change="onFiltersChange"
          >
            <!-- Resumen en una sola línea: "Todas" / "Todas menos X" / lista truncada -->
            <template v-slot:selection="{ index }">
              <span
                v-if="index === 0"
                class="selection-summary"
                :title="selectedLineas.join(', ')"
              >{{ lineasSummary }}</span>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-row dense align="center" class="mt-2">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedStatuses"
            :items="statusOptions"
            label="Estado contrato"
            multiple
            dense
            hide-details
            outlined
            class="single-line-select"
            @change="onFiltersChange"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index < 2"
                x-small
                class="mr-1"
                :color="statusColor(item)"
                text-color="white"
              >{{ item }}</v-chip>
              <span
                v-else-if="index === 2"
                class="selection-more"
              >+{{ selectedStatuses.length - 2 }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="6" md="4">
          <v-menu
            v-model="fromDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fromDate"
                label="Desde"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              :max="toDate"
              @input="onDateChange"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="6" md="4">
          <v-menu
            v-model="toDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="toDate"
                label="Hasta"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              :min="fromDate"
              :max="today"
              @input="onDateChange"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tabla -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loadingData"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [10, 20, 30, 50, 100] }"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="elevation-1"
        >
          <template v-slot:item.status="{ item }">
            <v-chip x-small :color="statusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
          </template>
          <template v-slot:item.revenue="{ item }">
            {{ formatAmount(item.revenue, item.currency) }}
          </template>
          <template v-slot:item.cost="{ item }">
            {{ formatAmount(item.cost, item.currency) }}
          </template>
          <template v-slot:item.margin="{ item }">
            <span :class="item.margin < 0 ? 'negative' : ''">{{ formatAmount(item.margin, item.currency) }}</span>
          </template>
          <template v-slot:item.marginPct="{ item }">
            <span :class="item.marginPct !== null && item.marginPct < 0 ? 'negative' : ''">{{ formatRate(item.marginPct) }}</span>
          </template>
          <template v-slot:item.lossRatio="{ item }">
            {{ formatRate(item.lossRatio) }}
          </template>
          <template v-slot:no-data>
            <div class="py-4">
              {{ selectedCountryId ? 'Sin resultados para los filtros seleccionados.' : 'Selecciona un país para ver la rentabilidad.' }}
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import CardResumeCompanyDetails from '../components/CardResumeCompanyDetails';
import ContractProfitabilityService from '../services/contract-profitability.service';
import CountryService from '../services/countries.service';

const CONTRACT_STATUSES = ['Activo', 'Churned', 'Pausado'];

const STATUS_COLORS = {
  Activo: 'green darken-1',
  Churned: 'red darken-1',
  Pausado: 'orange darken-2'
};

const BUSINESS_LINES = [
  'B2B Employee Benefit',
  'B2B Asistencias',
  'B2B2C Pago Directo',
  'B2B2C Planilla',
  'B2B2C Upsell',
  'B2C'
];

/** Fecha local (YYYY-MM-DD) sin desfase de zona horaria, tal como usan los v-date-picker. */
function toLocalDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default {
  name: 'ContractProfitability',
  mixins: [utilsMixin],
  components: { CardResumeCompanyDetails },
  data() {
    const today = new Date();
    // Rango por defecto: mes en curso (día 1 → hoy), consistente con el resto de las vistas mensuales.
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      countries: [],
      loadingCountries: false,
      selectedCountryId: null,
      lineaOptions: BUSINESS_LINES,
      selectedLineas: [...BUSINESS_LINES],
      statusOptions: CONTRACT_STATUSES,
      selectedStatuses: ['Activo'],
      today: toLocalDateString(today),
      fromDate: toLocalDateString(startOfMonth),
      toDate: toLocalDateString(today),
      fromDateMenu: false,
      toDateMenu: false,
      rows: [],
      loadingData: false,
      sortBy: 'lossRatio',
      sortDesc: true,
      headers: [
        { text: 'Código contrato', value: 'contratoCodigo' },
        { text: 'Empresa', value: 'companyName' },
        { text: 'Línea de negocio', value: 'lineaNegocio' },
        { text: 'Estado', value: 'status' },
        { text: 'Ingresos', value: 'revenue', sort: this.nullsLastSort },
        { text: 'Costos', value: 'cost', sort: this.nullsLastSort },
        { text: 'Margen', value: 'margin', sort: this.nullsLastSort },
        { text: 'Margen %', value: 'marginPct', sort: this.nullsLastSort },
        { text: 'Siniestralidad %', value: 'lossRatio', sort: this.nullsLastSort }
      ]
    };
  },
  computed: {
    // Texto de una sola línea para el select de líneas de negocio.
    lineasSummary() {
      const total = this.lineaOptions.length;
      const selected = this.selectedLineas.length;
      if (selected === 0) return '';
      if (selected === total) return 'Todas';
      if (selected === total - 1) {
        const excluded = this.lineaOptions.find(l => !this.selectedLineas.includes(l));
        return `Todas menos ${excluded}`;
      }
      return this.selectedLineas.join(', ');
    },
    grossMarginTotal() {
      return this.buildGrossMargin(this.rows);
    },
    siniestralidadTotal() {
      return this.buildSiniestralidad(this.rows);
    },
    siniestralidadAsistencias() {
      return this.buildSiniestralidad(
        this.rows.filter(r => r.lineaNegocio === 'B2B Asistencias')
      );
    },
    siniestralidadEmployeeBenefit() {
      return this.buildSiniestralidad(
        this.rows.filter(r => r.lineaNegocio === 'B2B Employee Benefit')
      );
    }
  },
  mounted() {
    this.loadCountries();
  },
  methods: {
    // Gross margin agregado = (Σingresos − Σcostos) / Σingresos sobre el subconjunto de filas.
    buildGrossMargin(rows) {
      if (!rows.length) return { pctLabel: '—', detailLabel: '' };
      const revenue = rows.reduce((acc, r) => acc + Number(r.revenue || 0), 0);
      const cost = rows.reduce((acc, r) => acc + Number(r.cost || 0), 0);
      const currency = rows[0].currency;
      const margin = revenue - cost;
      if (revenue <= 0) {
        return { pctLabel: '—', detailLabel: `Margen: ${this.formatAmount(margin, currency)}` };
      }
      const pct = (margin / revenue) * 100;
      return {
        pctLabel: pct.toFixed(2) + '%',
        detailLabel: `${this.formatAmount(margin, currency)} / ${this.formatAmount(revenue, currency)}`
      };
    },

    // Siniestralidad agregada = Σcostos / Σingresos sobre el subconjunto de filas.
    buildSiniestralidad(rows) {
      if (!rows.length) return { pctLabel: '—', detailLabel: '' };
      const revenue = rows.reduce((acc, r) => acc + Number(r.revenue || 0), 0);
      const cost = rows.reduce((acc, r) => acc + Number(r.cost || 0), 0);
      const currency = rows[0].currency;
      if (revenue <= 0) {
        return { pctLabel: '—', detailLabel: `Costos: ${this.formatAmount(cost, currency)}` };
      }
      const pct = (cost / revenue) * 100;
      return {
        pctLabel: pct.toFixed(2) + '%',
        detailLabel: `${this.formatAmount(cost, currency)} / ${this.formatAmount(revenue, currency)}`
      };
    },

    async loadCountries() {
      this.loadingCountries = true;
      try {
        const res = await CountryService.getAllCountries(true);
        if (res && res.status === 'ok') {
          this.countries = res.data.map(c => ({ text: c.name ?? '', id: c.id }));
          const chile = this.countries.find(c => (c.text || '').toLowerCase() === 'chile');
          if (chile && !this.selectedCountryId) {
            this.selectedCountryId = chile.id;
            this.loadData();
          }
        }
      } catch {
        this.showErrorSnackbar('No se pudieron cargar los países');
      }
      this.loadingCountries = false;
    },

    // Chips de línea de negocio / estado de contrato: cualquier cambio recarga la tabla.
    onFiltersChange() {
      if (this.selectedCountryId) {
        this.loadData();
      }
    },

    statusColor(status) {
      return STATUS_COLORS[status] || 'grey';
    },

    onDateChange() {
      this.fromDateMenu = false;
      this.toDateMenu = false;
      if (this.selectedCountryId) {
        this.loadData();
      }
    },

    async loadData() {
      if (!this.selectedCountryId) return;
      if (this.selectedLineas.length === 0 || this.selectedStatuses.length === 0) {
        this.rows = [];
        return;
      }
      this.loadingData = true;
      try {
        const res = await ContractProfitabilityService.getContractProfitability(
          this.selectedCountryId,
          this.selectedLineas,
          this.fromDate,
          this.toDate,
          this.selectedStatuses
        );
        if (res.status === 'ok') {
          this.rows = res.data.rows;
        } else {
          this.showErrorSnackbar('No se pudo cargar la rentabilidad por contrato');
        }
      } catch {
        this.showErrorSnackbar('No se pudo cargar la rentabilidad por contrato');
      }
      this.loadingData = false;
    },

    formatRate(rate) {
      if (rate === null || rate === undefined) return '—';
      return (Number(rate) * 100).toFixed(2) + '%';
    },

    formatAmount(value, currency) {
      if (value === null || value === undefined) return '—';
      const cur = currency || 'CLP';
      try {
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: cur,
          maximumFractionDigits: 0
        }).format(Number(value));
      } catch {
        return `${Number(value)} ${cur}`;
      }
    },

    nullsLastSort(a, b) {
      const va = a === null || a === undefined ? -Infinity : a;
      const vb = b === null || b === undefined ? -Infinity : b;
      return va - vb;
    }
  }
};
</script>

<style scoped>
.page-title {
  color: #104b94;
  font-weight: 700;
}
.negative {
  color: #d32f2f;
  font-weight: 600;
}
.filters-card {
  border-radius: 8px;
  background-color: #fff;
}
/* Mantiene la selección en una sola línea y la corta con puntos suspensivos. */
.single-line-select ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}
.selection-summary {
  display: block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.875rem;
}
.selection-more {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}
</style>
